import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import SyncIcon from '@mui/icons-material/Sync';
import { startFullResync } from '../db/replication';
import { useOnlineStatus } from '../features/sync/useOnlineStatus';
import { useSyncStatus } from '../features/sync/useSyncStatus';

/** Where the dialog stands: asking, working, or finished. */
type Phase = 'ask' | 'running' | 'done' | 'error';

/**
 * "Daten neu laden" — pull every document again, for a device that is missing
 * something the others can see.
 *
 * Worth its own dialog rather than a menu item that just acts: it is minutes of
 * traffic on a field tablet, so it asks first — and it is the one action a user
 * takes when they already distrust what they see, so it has to SHOW that it is
 * working and say when it is done. The bar counts finished areas rather than
 * documents: the client cannot know how many rows the server holds, and sixteen
 * honest steps beat one guessed percentage (see startFullResync).
 *
 * Local changes that have not reached the server are untouched — the re-pull
 * never touches the push side — which the dialog says outright, because that is
 * the fear that would otherwise stop someone from pressing it.
 */
export default function DataResyncDialog({
    open,
    onClose
}: {
    open: boolean;
    onClose: () => void;
}) {
    const online = useOnlineStatus();
    // Only the window that replicates can walk the collections: in any other
    // one the bar would sit at 0 of 16 for ever, because there is nothing there
    // to walk (see startFullResync).
    const { replicatingHere } = useSyncStatus();
    const [phase, setPhase] = useState<Phase>('ask');
    const [progress, setProgress] = useState({ done: 0, total: 0 });

    function close() {
        onClose();
        /**
         * The menu keeps this mounted, so it resets itself — but NOT while the
         * walk is still going: closing the window lets it finish in the
         * background (a field tablet on a thin line should not have to be
         * watched), and reopening should show where it got to rather than
         * offering to start a second one.
         */
        if (phase !== 'running') {
            setPhase('ask');
            setProgress({ done: 0, total: 0 });
        }
    }

    async function run() {
        setPhase('running');
        try {
            await startFullResync((done, total) =>
                setProgress({ done, total })
            );
            setPhase('done');
        } catch {
            setPhase('error');
        }
    }

    const percent =
        progress.total > 0 ? (progress.done / progress.total) * 100 : 0;

    return (
        <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
            <DialogTitle>
                <FormattedMessage id="resync.title" />
            </DialogTitle>
            <DialogContent>
                {phase === 'ask' && (
                    <>
                        <DialogContentText>
                            <FormattedMessage id="resync.explain" />
                        </DialogContentText>
                        {!online && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                <FormattedMessage id="resync.offline" />
                            </Alert>
                        )}
                        {online && !replicatingHere && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                <FormattedMessage id="resync.otherWindow" />
                            </Alert>
                        )}
                    </>
                )}
                {phase === 'running' && (
                    <Box>
                        <DialogContentText sx={{ mb: 2 }}>
                            <FormattedMessage id="resync.running" />
                        </DialogContentText>
                        <LinearProgress variant="determinate" value={percent} />
                        <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', mt: 1 }}
                            component="div"
                        >
                            <FormattedMessage
                                id="resync.progress"
                                values={progress}
                            />
                        </Typography>
                    </Box>
                )}
                {phase === 'done' && (
                    <Alert severity="success">
                        <FormattedMessage id="resync.done" />
                    </Alert>
                )}
                {phase === 'error' && (
                    <Alert severity="error">
                        <FormattedMessage id="resync.error" />
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    variant="outlined"
                    startIcon={<CloseIcon />}
                    onClick={close}
                >
                    <FormattedMessage
                        id={
                            phase === 'ask'
                                ? 'common.cancel'
                                : phase === 'running'
                                  ? 'resync.background'
                                  : 'common.close'
                        }
                    />
                </Button>
                {(phase === 'ask' || phase === 'error') && (
                    <Button
                        variant="contained"
                        startIcon={<SyncIcon />}
                        disabled={!online || !replicatingHere}
                        onClick={run}
                    >
                        <FormattedMessage id="resync.start" />
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
